// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import Accounts from '@app/features/auth/state/Accounts';
import Authentication from '@app/features/auth/state/Authentication';
import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import {channelNameToPlaintext} from '@app/features/channel/utils/ChannelNameText';
import {
	formatNewPostNotificationSuffix,
	resolveNewPostNotificationLevel,
} from '@app/features/forum/utils/ForumNotificationUtils';
import GuildMatureContentAgree from '@app/features/guild/state/GuildMatureContentAgree';
import Guilds from '@app/features/guild/state/Guilds';
import {GROUP_DM_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import GuildMembers from '@app/features/member/state/GuildMembers';
import {Message} from '@app/features/messaging/models/MessagingMessage';
import * as MessageUtils from '@app/features/messaging/utils/MessageUtils';
import Navigation from '@app/features/navigation/state/Navigation';
import SelectedChannel from '@app/features/navigation/state/SelectedChannel';
import {
	buildMessageNotificationBody,
	buildReactionSnippetPreview,
} from '@app/features/notification/utils/MessageNotificationPreview';
import {
	type NotificationClearKind,
	parseReactionNotificationTag,
	shouldCloseTrackedNotificationOnAck,
} from '@app/features/notification/utils/NotificationClearScope';
import {getNotificationIconURL} from '@app/features/notification/utils/NotificationIconURL';
import * as NotificationUtils from '@app/features/notification/utils/NotificationUtils';
import {
	ReactionNotificationDebouncer,
	type ReactionNotificationGroup,
} from '@app/features/notification/utils/ReactionNotificationDebouncer';
import {
	type ReactionNotificationMentionContext,
	resolveReactionNotificationEligibility,
} from '@app/features/notification/utils/ReactionNotificationEligibility';
import {
	isChannelMessageRowVisible,
	resolveFocusedReactionFlushAction,
} from '@app/features/notification/utils/ReactionNotificationViewport';
import * as PushSubscriptionService from '@app/features/platform/push/PushSubscriptionService';
import {IS_DEV} from '@app/features/platform/types/Env';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {makePersistent, stopPersistent} from '@app/features/platform/utils/MobXPersistence';
import {initializeStore} from '@app/features/platform/utils/StoreInitialization';
import LocalPresence from '@app/features/presence/state/LocalPresence';
import ReadStates from '@app/features/read_state/state/ReadStates';
import type {RelationshipWire} from '@app/features/relationship/models/Relationship';
import FriendsTab from '@app/features/relationship/state/FriendsTab';
import Relationships from '@app/features/relationship/state/Relationships';
import {FRIEND_ADDED_DESCRIPTOR} from '@app/features/relationship/utils/RelationshipMessageDescriptors';
import StreamerMode from '@app/features/streamer_mode/state/StreamerMode';
import ThreadMemberships from '@app/features/threads/state/ThreadMemberships';
import {isThreadMuted, resolveThreadNotificationLevel} from '@app/features/threads/utils/ThreadNotificationUtils';
import Dimension from '@app/features/ui/state/Dimension';
import Modal from '@app/features/ui/state/Modal';
import {isInstalledPwa} from '@app/features/ui/utils/PwaUtils';
import type {User} from '@app/features/user/models/User';
import UserGuildSettings from '@app/features/user/state/UserGuildSettings';
import UserSettings from '@app/features/user/state/UserSettings';
import Users from '@app/features/user/state/Users';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {FAVORITES_GUILD_ID as ME} from '@fluxer/constants/src/AppConstants';
import {ChannelTypes, MessageFlags, MessageTypes} from '@fluxer/constants/src/ChannelConstants';
import {MessageNotifications} from '@fluxer/constants/src/NotificationConstants';
import {StatusTypes} from '@fluxer/constants/src/StatusConstants';
import {RelationshipTypes} from '@fluxer/constants/src/UserConstants';
import type {Message as WireMessage} from '@fluxer/schema/src/domains/message/MessageResponseSchemas';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {LRUCache} from 'lru-cache';
import {makeAutoObservable, reaction, runInAction} from 'mobx';

const FRIEND_REQUEST_DESCRIPTOR = msg({
	message: 'Friend request',
	comment: 'Section or status label for a friend request item.',
});
const SENT_YOU_A_FRIEND_REQUEST_DESCRIPTOR = msg({
	message: '{displayName} sent you a friend request',
	comment: 'Toast title announcing an incoming friend request.',
});
const IS_NOW_YOUR_FRIEND_DESCRIPTOR = msg({
	message: '{displayName} is now your friend!',
	comment: 'Toast title announcing a newly accepted friend request.',
});
const REACTION_NOTIFICATION_BODY_DESCRIPTOR = msg({
	message: '{reactorName} reacted {emojiList}',
	comment:
		'Notification body for someone reacting to a message. reactorName is the display name of the person who reacted; emojiList is a space-separated list of the emoji they used (rendered as literal emoji characters, or :shortcode: for custom emoji), inserted by code.',
});
const REACTION_NOTIFICATION_BODY_SNIPPET_DESCRIPTOR = msg({
	message: '{reactorName} reacted {emojiList} to “{snippet}”',
	comment:
		'Notification body for someone reacting to a message, quoting the message. reactorName is the display name of the person who reacted; emojiList is a space-separated list of the emoji they used; snippet is a short excerpt of the message that was reacted to. All inserted by code.',
});
const REACTION_NOTIFICATION_BODY_NO_TEXT_DESCRIPTOR = msg({
	message: '{reactorName} reacted {emojiList} to a message with no text',
	comment:
		'Notification body for someone reacting to a message that has no text to quote -- it holds only an attachment, sticker or embed. reactorName is the display name of the person who reacted; emojiList is a space-separated list of the emoji they used, inserted by code.',
});
const REACTION_NOTIFICATION_COUNT_DESCRIPTOR = msg({
	message: '{reactorName} reacted with {emojiCount, plural, one {# emoji} other {# emojis}}',
	comment:
		'Notification body for someone reacting to a message with more distinct emoji than can be listed individually. reactorName is the display name of the person who reacted; emojiCount is how many distinct emoji they used.',
});
const logger = new Logger('Notification');
const shouldManagePushSubscriptions = (): boolean => isInstalledPwa();

function guildChannelNotificationName(channel: Channel): string {
	const prefix = channel.type === ChannelTypes.GUILD_VOICE ? '' : '#';
	return `${prefix}${channelNameToPlaintext(channel.name ?? '')}`;
}

export enum TTSNotificationMode {
	FOR_ALL_CHANNELS = 0,
	FOR_CURRENT_CHANNEL = 1,
	NEVER = 2,
}

const MAX_PER_CHANNEL = 5;
const CACHE_SIZE = 500;
const REACTION_EMOJI_DISPLAY_CAP = 4;
const REACTION_VISIBILITY_SWEEP_DELAY_MS = 500;

interface TrackedNotification {
	kind: NotificationClearKind;
	messageId: string;
	tag: string | null;
	browserNotification: Notification | null;
	nativeId: string | null;
}

function closeTrackedNotifications(tracked: ReadonlyArray<TrackedNotification>): void {
	const nativeIds: Array<string> = [];
	for (const entry of tracked) {
		if (entry.nativeId != null) {
			nativeIds.push(entry.nativeId);
			continue;
		}
		if (entry.browserNotification != null) {
			entry.browserNotification.close();
			continue;
		}
		if (entry.tag != null) {
			void NotificationUtils.closeServiceWorkerNotifications({tag: entry.tag});
		}
	}
	if (nativeIds.length > 0) {
		NotificationUtils.closeNativeNotifications(nativeIds);
	}
}

const notificationTracker = new (class {
	private channels: Record<string, Array<TrackedNotification>> = {};

	track(channelId: string, notification: TrackedNotification): void {
		let notifications = this.channels[channelId];
		if (notifications == null) {
			notifications = [];
			this.channels[channelId] = notifications;
		}
		notifications.push(notification);
		while (notifications.length > MAX_PER_CHANNEL) {
			const old = notifications.shift();
			if (old) {
				closeTrackedNotifications([old]);
			}
		}
	}

	clearChannelUpToAck(channelId: string, ackMessageId: string | null): void {
		this.removeMatching(channelId, (tracked) =>
			shouldCloseTrackedNotificationOnAck({kind: tracked.kind, messageId: tracked.messageId, ackMessageId}),
		);
	}

	clearMessage(channelId: string, messageId: string): void {
		this.removeMatching(channelId, (tracked) => tracked.messageId === messageId);
	}

	clearVisibleReactions(channelId: string, isMessageVisible: (messageId: string) => boolean): void {
		this.removeMatching(channelId, (tracked) => tracked.kind === 'reaction' && isMessageVisible(tracked.messageId));
	}

	private removeMatching(channelId: string, predicate: (tracked: TrackedNotification) => boolean): void {
		const notifications = this.channels[channelId];
		if (notifications == null) return;
		const closing: Array<TrackedNotification> = [];
		const keeping: Array<TrackedNotification> = [];
		for (const tracked of notifications) {
			(predicate(tracked) ? closing : keeping).push(tracked);
		}
		if (closing.length === 0) return;
		if (keeping.length === 0) {
			delete this.channels[channelId];
		} else {
			this.channels[channelId] = keeping;
		}
		closeTrackedNotifications(closing);
	}
})();

type NotificationData = Readonly<{
	message: WireMessage;
	messageRecord: Message;
	currentUser: User;
	user: User;
	channel: Channel;
}>;

export interface MessageReactionAddNotificationPayload {
	channelId: string;
	messageId: string;
	reactorId: string;
	emoji: string;
	messageAuthorId?: string;
	mentionEveryone: boolean;
	mentionUserIds: ReadonlyArray<string>;
	mentionRoleIds: ReadonlyArray<string>;
	flags: number;
	content?: string;
}

export interface MessageReactionRemoveNotificationPayload {
	messageId: string;
	reactorId: string;
	emoji: string;
}

const isDocumentFocused = (): boolean => {
	if (typeof document === 'undefined') {
		return false;
	}
	if (document.hidden) {
		return false;
	}
	return typeof document.hasFocus === 'function' && document.hasFocus();
};

class NotificationState {
	browserNotificationsEnabled = false;
	unreadMessageBadgeEnabled = true;
	ttsNotificationMode: TTSNotificationMode = TTSNotificationMode.NEVER;
	focused = isDocumentFocused();
	notifiedMessageIds = new LRUCache<string, boolean>({max: CACHE_SIZE});
	private isPersisting = false;
	private accountReactionDisposer: (() => void) | null = null;
	private reactionVisibilityDisposer: (() => void) | null = null;
	private reactionSweepTimer: ReturnType<typeof setTimeout> | null = null;
	private i18n: I18n | null = null;
	reactionDebouncer = new ReactionNotificationDebouncer((group) => {
		this.handleReactionNotificationFlush(group);
	});

	constructor() {
		makeAutoObservable<this, 'reactionSweepTimer'>(
			this,
			{
				notifiedMessageIds: false,
				reactionDebouncer: false,
				reactionSweepTimer: false,
			},
			{autoBind: true},
		);
		initializeStore(this, async () => {
			await this.initPersistence();
			await this.refreshPermission();
		});
		queueMicrotask(() => {
			NotificationUtils.ensureDesktopNotificationClickHandler();
		});
		queueMicrotask(() => {
			this.accountReactionDisposer = reaction(
				() => {
					try {
						return Accounts?.currentUserId;
					} catch {
						return undefined;
					}
				},
				() => {
					if (!shouldManagePushSubscriptions()) return;
					if (!this.browserNotificationsEnabled) return;
					void PushSubscriptionService.registerPushSubscription();
				},
			);
		});
		queueMicrotask(() => {
			this.reactionVisibilityDisposer = reaction(
				() => {
					const channelId = Navigation.channelId ?? SelectedChannel.currentChannelId;
					if (channelId == null) return null;
					const dimensions = Dimension.channelDimensionsFor(channelId);
					return `${channelId}:${dimensions?.scrollTop ?? ''}:${dimensions?.scrollHeight ?? ''}:${this.focused}`;
				},
				(key) => {
					if (key == null) return;
					this.scheduleReactionVisibilitySweep();
				},
			);
		});
		if (IS_DEV) {
			window.__notificationCleanup = () => this.cleanup();
		}
	}

	setI18n(i18n: I18n): void {
		this.i18n = i18n;
	}

	private async initPersistence(): Promise<void> {
		if (this.isPersisting) return;
		this.isPersisting = true;
		await makePersistent(this, 'Notification', [
			'browserNotificationsEnabled',
			'unreadMessageBadgeEnabled',
			'ttsNotificationMode',
		]);
	}

	private cleanup(): void {
		if (!this.isPersisting) return;
		stopPersistent('Notification', this);
		this.isPersisting = false;
		this.accountReactionDisposer?.();
		this.accountReactionDisposer = null;
		this.reactionVisibilityDisposer?.();
		this.reactionVisibilityDisposer = null;
		if (this.reactionSweepTimer != null) {
			clearTimeout(this.reactionSweepTimer);
			this.reactionSweepTimer = null;
		}
		this.reactionDebouncer.dispose();
	}

	getTTSNotificationMode(): TTSNotificationMode {
		return this.ttsNotificationMode;
	}

	setTTSNotificationMode(mode: TTSNotificationMode): void {
		this.ttsNotificationMode = mode;
	}

	private isMessageMentionLike(channel: Channel, message: Message, currentUser: User, ignoreEveryone = false): boolean {
		if (MessageUtils.isMentioned(currentUser, message, ignoreEveryone)) {
			return true;
		}
		if (channel.isPrivate()) {
			return !UserGuildSettings.isGuildOrChannelMuted(null, channel.id);
		}
		return false;
	}

	private shouldNotifyBasedOnSettings(channel: Channel, messageRecord: Message, currentUser: User): boolean {
		if (channel.isThread()) {
			const isMember = ThreadMemberships.isMember(channel.id);
			const threadLevel = isMember
				? resolveThreadNotificationLevel(channel)
				: (resolveNewPostNotificationLevel(channel, messageRecord.id) ?? MessageNotifications.ONLY_MENTIONS);
			if (threadLevel === MessageNotifications.NO_MESSAGES) return false;
			if (threadLevel === MessageNotifications.ALL_MESSAGES) return true;
			return this.isMessageMentionLike(channel, messageRecord, currentUser, !isMember);
		}
		const level = UserGuildSettings.resolveEffectiveMessageNotifications({
			id: channel.id,
			guildId: channel.guildId,
			parentId: channel.parentId ?? undefined,
			type: channel.type,
		});
		if (level === MessageNotifications.NO_MESSAGES) {
			return false;
		}
		if (level === MessageNotifications.ALL_MESSAGES) {
			return true;
		}
		return this.isMessageMentionLike(channel, messageRecord, currentUser);
	}

	private isFocusedForNotifications(): boolean {
		return isDocumentFocused() || this.focused;
	}

	private getVisibleChannelId(): string | null {
		return Navigation.channelId ?? SelectedChannel.currentChannelId;
	}

	isViewingChannel(channelId: string): boolean {
		return this.getVisibleChannelId() === channelId || Navigation.threadId === channelId;
	}

	private sweepReactionNotifications(channelId: string): void {
		notificationTracker.clearVisibleReactions(channelId, (messageId) =>
			isChannelMessageRowVisible(channelId, messageId),
		);
		void NotificationUtils.closeServiceWorkerNotifications({
			match: (notification) => {
				const parsed = parseReactionNotificationTag(notification.tag);
				if (parsed == null) return false;
				if (parsed.channelId != null && parsed.channelId !== channelId) return false;
				return isChannelMessageRowVisible(channelId, parsed.messageId);
			},
		});
	}

	private scheduleReactionVisibilitySweep(): void {
		if (!this.browserNotificationsEnabled) return;
		if (this.reactionSweepTimer != null) return;
		this.reactionSweepTimer = setTimeout(() => {
			this.reactionSweepTimer = null;
			if (!this.isFocusedForNotifications()) return;
			const channelId = this.getVisibleChannelId();
			if (channelId == null) return;
			this.sweepReactionNotifications(channelId);
		}, REACTION_VISIBILITY_SWEEP_DELAY_MS);
	}

	private validateNotificationData(message: WireMessage): NotificationData | null {
		if (StreamerMode.shouldDisableNotifications) return null;
		const channel = Channels.getChannel(message.channel_id);
		if (!channel) return null;
		if (message.author.id === Authentication.currentUserId) return null;
		if (Relationships.isBlocked(message.author.id)) return null;
		if (LocalPresence.getStatus() === StatusTypes.DND) return null;
		if ((message.flags & MessageFlags.SUPPRESS_NOTIFICATIONS) === MessageFlags.SUPPRESS_NOTIFICATIONS) {
			return null;
		}
		if (channel.isThread()) {
			if (isThreadMuted(channel)) return null;
		} else if (
			UserGuildSettings.resolvesToNoMessages({
				id: channel.id,
				guildId: channel.guildId,
				parentId: channel.parentId ?? undefined,
				type: channel.type,
			})
		) {
			return null;
		}
		if (this.notifiedMessageIds.has(message.id)) {
			return null;
		}
		if (GuildMatureContentAgree.shouldShowGate({channelId: channel.id, guildId: channel.guildId ?? null})) {
			return null;
		}
		const currentUser = Users.getCurrentUser();
		if (!currentUser) return null;
		const messageRecord = new Message(message, {skipUserCache: false, missingReactions: 'preserve'});
		return {message, messageRecord, currentUser, user: messageRecord.author, channel};
	}

	private markNotified(key: string): void {
		this.notifiedMessageIds.set(key, true);
	}

	private claimNotification(key: string): boolean {
		if (this.notifiedMessageIds.has(key)) return false;
		this.markNotified(key);
		return true;
	}

	private async showBackgroundNotification(data: NotificationData): Promise<void> {
		if (!this.i18n) {
			throw new Error('Notification: i18n not initialized');
		}
		const i18n = this.i18n;
		const {message, user, channel} = data;
		if (channel.isPrivate()) {
			NotificationUtils.playDirectMessageNotificationSoundIfEnabled();
		} else {
			NotificationUtils.playNotificationSoundIfEnabled();
		}
		if (!this.browserNotificationsEnabled) {
			return;
		}
		const useMacOSNotificationPresentation = NotificationUtils.isMacOSDesktopNotification();
		let title = NicknameUtils.getNickname(user, channel.guildId, channel.id);
		let subtitle: string | undefined;
		switch (channel.type) {
			case ChannelTypes.GUILD_TEXT:
			case ChannelTypes.GUILD_ANNOUNCEMENT:
			case ChannelTypes.GUILD_VOICE:
				if (message.type === MessageTypes.DEFAULT) {
					if (useMacOSNotificationPresentation) {
						const guild = channel.guildId ? Guilds.getGuild(channel.guildId) : null;
						const channelLabel = guildChannelNotificationName(channel);
						subtitle = guild ? `${guild.name} ${channelLabel}` : channelLabel;
					} else {
						title = `${title} (${guildChannelNotificationName(channel)})`;
					}
				} else {
					const guild = channel.guildId ? Guilds.getGuild(channel.guildId) : null;
					if (guild) {
						if (useMacOSNotificationPresentation) {
							title = guild.name;
							subtitle = guildChannelNotificationName(channel);
						} else {
							title = `${guild.name} (${guildChannelNotificationName(channel)})`;
						}
					}
				}
				break;
			case ChannelTypes.ANNOUNCEMENT_THREAD:
			case ChannelTypes.PUBLIC_THREAD:
			case ChannelTypes.PRIVATE_THREAD: {
				const parent = channel.parentId ? Channels.getChannel(channel.parentId) : undefined;
				const guild = channel.guildId ? Guilds.getGuild(channel.guildId) : null;
				const context =
					formatNewPostNotificationSuffix(i18n, channel, message.id) ??
					[`#${channel.name}`, parent ? `#${parent.name}` : null, guild?.name ?? null]
						.filter((part) => part != null)
						.join(', ');
				if (useMacOSNotificationPresentation) {
					subtitle = context;
				} else {
					title = `${title} (${context})`;
				}
				break;
			}
			case ChannelTypes.GROUP_DM: {
				const groupDmName = channel.name || i18n._(GROUP_DM_DESCRIPTOR);
				if (useMacOSNotificationPresentation) {
					subtitle = groupDmName;
				} else {
					title = `${title} (${groupDmName})`;
				}
				break;
			}
		}
		const body = buildMessageNotificationBody(data.messageRecord, i18n);
		const notificationUrl =
			channel.guildId && channel.guildId !== ME
				? Routes.channelMessage(channel.guildId, channel.id, message.id)
				: Routes.dmChannelMessage(channel.id, message.id);
		try {
			const result = await NotificationUtils.showNotification({
				id: message.id,
				title,
				subtitle,
				body,
				icon: getNotificationIconURL(user, channel.guildId),
				url: notificationUrl,
				playSound: false,
				accountKey: Accounts.currentAccountKey,
				channelId: channel.id,
				messageId: message.id,
			});
			notificationTracker.track(channel.id, {
				kind: 'message',
				messageId: message.id,
				tag: message.id,
				browserNotification: result.browserNotification,
				nativeId: result.nativeNotificationId,
			});
		} catch (error) {
			logger.error('Failed to show notification', {messageId: message.id, channelId: channel.id}, error);
		}
	}

	private playForegroundObscuredNotificationSound(channel: Channel): void {
		if (channel.isPrivate()) {
			NotificationUtils.playDirectMessageNotificationSoundIfEnabled();
		} else {
			NotificationUtils.playNotificationSoundIfEnabled();
		}
	}

	private buildReactionMentionContext(channel: Channel, currentUser: User): ReactionNotificationMentionContext {
		const everyoneMentionSuppressed = UserGuildSettings.isEveryoneMentionSuppressed(channel.guildId ?? null);
		if (channel.guildId == null) {
			return {
				everyoneMentionSuppressed,
				isGuildChannel: false,
				currentUserGuildRoleIds: null,
				roleMentionSuppressed: false,
				isUnmutedPrivateChannel: channel.isPrivate()
					? !UserGuildSettings.isGuildOrChannelMuted(null, channel.id)
					: false,
			};
		}
		const guild = Guilds.getGuild(channel.guildId);
		const guildMember = guild ? GuildMembers.getMember(guild.id, currentUser.id) : undefined;
		return {
			everyoneMentionSuppressed,
			isGuildChannel: true,
			currentUserGuildRoleIds: guildMember ? Array.from(guildMember.roles) : null,
			roleMentionSuppressed: UserGuildSettings.isRoleMentionSuppressed(channel.guildId),
			isUnmutedPrivateChannel: false,
		};
	}

	private buildReactionNotificationBody(
		reactorName: string,
		emojis: ReadonlyArray<string>,
		snippet: string | undefined,
		i18n: I18n,
	): string {
		if (emojis.length > REACTION_EMOJI_DISPLAY_CAP) {
			return i18n._(REACTION_NOTIFICATION_COUNT_DESCRIPTOR, {reactorName, emojiCount: emojis.length});
		}
		const emojiList = emojis.join(' ');
		if (snippet == null) {
			return i18n._(REACTION_NOTIFICATION_BODY_DESCRIPTOR, {reactorName, emojiList});
		}
		if (snippet.length === 0) {
			return i18n._(REACTION_NOTIFICATION_BODY_NO_TEXT_DESCRIPTOR, {reactorName, emojiList});
		}
		return i18n._(REACTION_NOTIFICATION_BODY_SNIPPET_DESCRIPTOR, {reactorName, emojiList, snippet});
	}

	private resolveReactionSnippet(content: string | undefined, channelId: string, i18n: I18n): string | undefined {
		if (content === undefined) return undefined;
		if (content.length === 0) return '';
		return buildReactionSnippetPreview(content, {channelId, i18n});
	}

	private async showReactionNotification(
		group: ReactionNotificationGroup,
		channel: Channel,
		reactor: User,
	): Promise<void> {
		if (!this.i18n) {
			throw new Error('Notification: i18n not initialized');
		}
		if (!this.browserNotificationsEnabled) {
			return;
		}
		const reactorName = NicknameUtils.getNickname(reactor, channel.guildId, channel.id);
		const snippet = this.resolveReactionSnippet(group.content, channel.id, this.i18n);
		const body = this.buildReactionNotificationBody(reactorName, group.emojis, snippet, this.i18n);
		const notificationUrl =
			channel.guildId && channel.guildId !== ME
				? Routes.channelMessage(channel.guildId, channel.id, group.messageId)
				: Routes.dmChannelMessage(channel.id, group.messageId);
		const tag = `reaction:${group.messageId}:${group.reactorId}`;
		try {
			const result = await NotificationUtils.showNotification({
				id: tag,
				title: reactorName,
				body,
				icon: getNotificationIconURL(reactor, channel.guildId),
				url: notificationUrl,
				playSound: !group.isReplacement,
				channelId: channel.id,
				messageId: group.messageId,
			});
			notificationTracker.track(channel.id, {
				kind: 'reaction',
				messageId: group.messageId,
				tag,
				browserNotification: result.browserNotification,
				nativeId: result.nativeNotificationId,
			});
		} catch (error) {
			logger.error('Failed to show reaction notification', {messageId: group.messageId, channelId: channel.id}, error);
		}
	}

	private handleReactionNotificationFlush(group: ReactionNotificationGroup): void {
		const channel = Channels.getChannel(group.channelId);
		if (!channel) return;
		const isFocusedViewingChannel = this.isViewingChannel(group.channelId) && this.isFocusedForNotifications();
		if (isFocusedViewingChannel) {
			const action = resolveFocusedReactionFlushAction({
				hasModalOpen: Modal.hasModalOpen(),
				isMessageRowVisible: isChannelMessageRowVisible(group.channelId, group.messageId),
			});
			if (action === 'obscuredSound') {
				this.playForegroundObscuredNotificationSound(channel);
				return;
			}
			if (action === 'inChannelSound') {
				NotificationUtils.playSameChannelNotificationSoundIfEnabled();
				return;
			}
		}
		// Only the popup needs the reactor (name + icon); the focused-channel sound paths above
		// do not, so an uncached reactor must not silence them.
		const reactor = Users.getUser(group.reactorId);
		if (!reactor) return;
		void this.showReactionNotification(group, channel, reactor);
	}

	handleMessageCreate({message}: {message: WireMessage}): boolean {
		if (StreamerMode.shouldDisableNotifications) {
			return false;
		}
		const isFocusedViewingChannel =
			message.author.id !== Authentication.currentUserId &&
			!Relationships.isBlocked(message.author.id) &&
			Channels.getChannel(message.channel_id) != null &&
			this.isViewingChannel(message.channel_id) &&
			this.isFocusedForNotifications();
		if (isFocusedViewingChannel && !Modal.hasModalOpen()) {
			NotificationUtils.playSameChannelNotificationSoundIfEnabled();
			this.markNotified(message.id);
			return true;
		}
		const notificationData = this.validateNotificationData(message);
		if (!notificationData) {
			return false;
		}
		const {channel, messageRecord, currentUser} = notificationData;
		if (!this.shouldNotifyBasedOnSettings(channel, messageRecord, currentUser)) {
			return false;
		}
		if (!this.claimNotification(message.id)) {
			return false;
		}
		if (isFocusedViewingChannel && Modal.hasModalOpen()) {
			this.playForegroundObscuredNotificationSound(channel);
			return true;
		}
		void this.showBackgroundNotification(notificationData);
		return true;
	}

	handleMessageReactionAdd(payload: MessageReactionAddNotificationPayload): boolean {
		const channel = Channels.getChannel(payload.channelId);
		if (!channel) return false;
		const currentUser = Users.getCurrentUser();
		if (!currentUser) return false;
		const channelShape = {
			id: channel.id,
			guildId: channel.guildId,
			parentId: channel.parentId ?? undefined,
			type: channel.type,
		};

		const eligible = resolveReactionNotificationEligibility({
			currentUserId: currentUser.id,
			reactorId: payload.reactorId,
			messageAuthorId: payload.messageAuthorId,
			isBlocked: Relationships.isBlocked(payload.reactorId),
			isDnd: LocalPresence.getStatus() === StatusTypes.DND,
			streamerModeSuppressed: StreamerMode.shouldDisableNotifications,
			channelMutesMessages: UserGuildSettings.resolvesToNoMessages(channelShape),
			notifyReactionsOwnMessages: UserSettings.notifyReactionsOwnMessages,
			notifyReactionsNotifiedMessages: UserGuildSettings.resolveEffectiveNotifyReactionsNotifiedMessages(channelShape),
			notificationLevel: UserGuildSettings.resolveEffectiveMessageNotifications(channelShape),
			mentionSnapshot: {
				mentionEveryone: payload.mentionEveryone,
				mentionUserIds: payload.mentionUserIds,
				mentionRoleIds: payload.mentionRoleIds,
				flags: payload.flags,
			},
			mentionContext: this.buildReactionMentionContext(channel, currentUser),
		});

		if (!eligible) {
			return false;
		}

		this.reactionDebouncer.add({
			channelId: payload.channelId,
			messageId: payload.messageId,
			reactorId: payload.reactorId,
			emoji: payload.emoji,
			content: payload.content,
		});
		return true;
	}

	handleMessageReactionRemove(payload: MessageReactionRemoveNotificationPayload): void {
		this.reactionDebouncer.remove({
			messageId: payload.messageId,
			reactorId: payload.reactorId,
			emoji: payload.emoji,
		});
	}

	handleNotificationPermissionGranted(): void {
		this.browserNotificationsEnabled = true;
		if (shouldManagePushSubscriptions()) {
			void PushSubscriptionService.registerPushSubscription();
		}
	}

	handleNotificationPermissionDenied(): void {
		this.browserNotificationsEnabled = false;
		if (shouldManagePushSubscriptions()) {
			void PushSubscriptionService.unregisterAllPushSubscriptions();
		}
	}

	async refreshPermission(): Promise<void> {
		try {
			const granted = await NotificationUtils.isGranted();
			if (!granted) {
				runInAction(() => {
					this.browserNotificationsEnabled = false;
				});
				return;
			}
			if (this.browserNotificationsEnabled && shouldManagePushSubscriptions()) {
				void PushSubscriptionService.registerPushSubscription();
			}
		} catch (error) {
			logger.error('Failed to refresh notification permission', error);
		}
	}

	handleNotificationSoundToggle(enabled: boolean): void {
		this.unreadMessageBadgeEnabled = enabled;
	}

	handleWindowFocused({focused}: {focused: boolean}): void {
		this.focused = focused;
		if (!focused) return;
		const channelId = SelectedChannel.currentChannelId;
		if (channelId == null) return;
		notificationTracker.clearChannelUpToAck(channelId, ReadStates.ackMessageId(channelId));
		this.scheduleReactionVisibilitySweep();
	}

	handleChannelSelect({channelId}: {channelId?: string | null}): void {
		if (!channelId) return;
		notificationTracker.clearChannelUpToAck(channelId, ReadStates.ackMessageId(channelId));
		this.scheduleReactionVisibilitySweep();
	}

	handleMessageAck({channelId, messageId}: {channelId: string; messageId?: string | null}): void {
		notificationTracker.clearChannelUpToAck(channelId, messageId ?? null);
	}

	handleMessageDelete({channelId, messageId}: {channelId: string; messageId: string}): void {
		notificationTracker.clearMessage(channelId, messageId);
	}

	handleRelationshipNotification(
		relationship: RelationshipWire,
		options?: {
			event?: 'add' | 'update';
		},
	): void {
		if (RuntimeConfig.directMessagesDisabled) {
			return;
		}
		if (StreamerMode.shouldDisableNotifications) {
			return;
		}
		if (!this.i18n) {
			throw new Error('Notification: i18n not initialized');
		}
		if (!this.browserNotificationsEnabled) {
			return;
		}
		if (LocalPresence.getStatus() === StatusTypes.DND) {
			return;
		}
		const user = Users.getUser(relationship.user?.id ?? relationship.id);
		if (!user) {
			return;
		}
		const cacheKey = `relationship_${relationship.type}_${user.id}`;
		if (this.notifiedMessageIds.has(cacheKey)) {
			return;
		}
		if (options?.event === 'update') {
			return;
		}
		let title: string;
		let body: string;
		const displayName = NicknameUtils.getDisplayName(user);
		if (relationship.type === RelationshipTypes.INCOMING_REQUEST) {
			title = this.i18n._(FRIEND_REQUEST_DESCRIPTOR);
			body = this.i18n._(SENT_YOU_A_FRIEND_REQUEST_DESCRIPTOR, {displayName});
			FriendsTab.setTab('pending');
		} else if (relationship.type === RelationshipTypes.FRIEND) {
			title = this.i18n._(FRIEND_ADDED_DESCRIPTOR);
			body = this.i18n._(IS_NOW_YOUR_FRIEND_DESCRIPTOR, {displayName});
		} else {
			return;
		}
		if (this.focused && NotificationUtils.isMacOSDesktopNotification()) {
			this.markNotified(cacheKey);
			return;
		}
		void NotificationUtils.showNotification({
			id: cacheKey,
			title,
			body,
			icon: getNotificationIconURL(user),
			url: Routes.ME,
			accountKey: Accounts.currentAccountKey,
		}).catch((error) => {
			logger.error('Failed to show relationship notification', {cacheKey}, error);
		});
		this.markNotified(cacheKey);
	}
}

export default new NotificationState();
